import React, { useState } from 'react';
import { useHistory } from 'react-router';

import './index.less';

import { ProjectModel } from '../../../../../gamma-api';
import IbCard from '../../../../components/common/IbCard';
import IbContextMenu, { IIbContextMenuItem } from '../../../../components/common/IbContextMenu';
import IbButton from '../../../../components/common/IbButton';
import IbIcon from '../../../../components/common/IbIcon';
import IbTypography from '../../../../components/common/IbTypography';
import IbTextHighlighter from '../../../../components/common/IbTextHighlighter';
import { getTranslateFunc } from '../../../../utils/stringUtil';
import ImportUpload from '../../../../components/ImportUpload';
import ExportButton from '../../../../components/ExportButton';
import { getProjectPath } from '../../../../utils/pathUtils';

import {
  COPY_ID_BUTTON_CLASS_NAME,
  EXTRA_CLASS_NAME,
  FOOTER_CLASS_NAME,
  MAIN_CLASS_NAME,
  TRANSLATION_GROUP_PREFIX,
} from './const';

interface IProjectCardProps {
  project: ProjectModel;
  searchText: string;
  onEditProject: () => void;
  onDeleteProject: () => void;
  onProjectClick: () => void;
}

const ProjectCard: React.FC<IProjectCardProps> = ({
  project,
  searchText,
  onEditProject,
  onDeleteProject,
  onProjectClick,
}) => {
  const translate = getTranslateFunc(TRANSLATION_GROUP_PREFIX);

  const { push } = useHistory();

  const [contextMenuVisible, setContextMenuVisible] = useState(false);

  const goToProject = () => {
    push(getProjectPath(project.folder_id, project.project_id));
  };

  const onImportFileFinish = async () => {
    goToProject();
  };

  const onCopyIdButtonClick = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e.stopPropagation();
    navigator.clipboard.writeText(project.external_id);
  };

  const contextMenuItems = [
    {
      icon: <IbIcon iconName="efferent-three" size={20} />,
      text: translate('Context: Export'),
      render: (child) => (
        <ExportButton.ProjectExport projectId={project.project_id}>{child}</ExportButton.ProjectExport>
      ),
    },
    {
      icon: <IbIcon iconName="afferent-three" size={20} />,
      text: translate('Context: Import'),
      render: (child) => (
        <ImportUpload.ProjectImport
          folderId={project.folder_id}
          projectId={project.project_id}
          onFinish={onImportFileFinish}
        >
          {child}
        </ImportUpload.ProjectImport>
      ),
    },
    {
      icon: <IbIcon iconName="edit" size={20} />,
      onSelect: onEditProject,
      text: translate('Context: Edit'),
    },
    {
      icon: <IbIcon iconName="delete" size={20} />,
      onSelect: onDeleteProject,
      text: translate('Context: Delete'),
    },
  ] as IIbContextMenuItem[];

  const renderExtra = () => {
    const onContextMenuClose = () => setContextMenuVisible(false);
    const onMenuButtonClick = () => setContextMenuVisible(true);
    return (
      <div
        className={EXTRA_CLASS_NAME}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <IbContextMenu menuItems={contextMenuItems} visible={contextMenuVisible} onClose={onContextMenuClose}>
          <IbButton icon={<IbIcon iconName="more-one" size={24} />} type="icon" onClick={onMenuButtonClick} />
        </IbContextMenu>
      </div>
    );
  };

  const renderFooter = () => {
    return (
      <div className={FOOTER_CLASS_NAME}>
        <IbTypography.Paragraph disabled>
          <IbTextHighlighter highlightedText={searchText} text={`ID: ${project.external_id}`} />
        </IbTypography.Paragraph>
        <IbButton
          className={COPY_ID_BUTTON_CLASS_NAME}
          icon={<IbIcon iconName="copy-one" />}
          type="link"
          onClick={onCopyIdButtonClick}
        />
      </div>
    );
  };

  return (
    <div className={MAIN_CLASS_NAME}>
      <IbCard
        extra={renderExtra()}
        footer={renderFooter()}
        searchText={searchText}
        title={project.name}
        onClick={onProjectClick}
      ></IbCard>
    </div>
  );
};

export default ProjectCard;
